import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Input } from '@/components/ui/input';
import { adminApi } from '@/utils/adminApi';
import { BedDouble, Building2, Search, Users } from 'lucide-react';

export default function AdminPropertiesPage() {
  const [pgs, setPgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchPgs = async () => {
      try {
        const response = await adminApi.getPgs();
        setPgs(response.data);
      } catch (error) {
        toast.error(error.message || 'Failed to load properties');
      } finally {
        setLoading(false);
      }
    };

    fetchPgs();
  }, []);

  const filteredPgs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return pgs;
    }

    return pgs.filter((pg) =>
      [pg.name, pg.ownerName, pg.address]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [pgs, searchTerm]);

  return (
    <AdminLayout>
      <div className="space-y-6 lg:space-y-8">
        <section className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)] sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Properties</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            See all active PG inventory across the platform.
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg">
            Review every PG with its owner, bed inventory, active tenants, and occupancy footprint from one responsive platform view.
          </p>
        </section>

        <section className="min-w-0 rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h3 className="text-2xl font-bold text-slate-950">Property directory</h3>
              <p className="mt-1 text-slate-600">{pgs.length} total active PGs</p>
            </div>

            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by property, owner, or address"
                className="h-12 rounded-2xl border-slate-200 pl-11"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-14">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-slate-950" />
            </div>
          ) : (
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {filteredPgs.map((pg) => (
                <div key={pg.id} className="min-w-0 rounded-[30px] border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="break-words text-xl font-bold text-slate-950">{pg.name}</p>
                      <p className="mt-1 break-words text-sm text-slate-500">{pg.ownerName}</p>
                    </div>
                    <div className="flex-shrink-0 rounded-2xl bg-white p-3 shadow-sm">
                      <Building2 className="h-5 w-5 text-emerald-700" />
                    </div>
                  </div>

                  <p className="mt-4 break-words text-sm leading-6 text-slate-600">{pg.address || 'No address added'}</p>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <BedDouble className="h-4 w-4 text-cyan-700" />
                        Beds
                      </div>
                      <p className="mt-2 text-2xl font-bold text-slate-950">{pg.totalBeds}</p>
                      <p className="text-sm text-slate-500">{pg.occupiedBeds} occupied</p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Users className="h-4 w-4 text-emerald-700" />
                        Tenants
                      </div>
                      <p className="mt-2 text-2xl font-bold text-slate-950">{pg.activeTenants}</p>
                      <p className="text-sm text-slate-500">{pg.totalRooms} rooms, {pg.totalFloors} floors</p>
                    </div>
                  </div>
                </div>
              ))}

              {filteredPgs.length === 0 && (
                <div className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-slate-500 xl:col-span-2">
                  No properties matched your search.
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
